import { useCallback, useRef } from "react";

import { getItemsWithMissingDataByFieldId } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { getEarliestItemIdWithMissingData } from "pages/app/field-timeline/utils/TimelineUtils";

const CARD_ANIMATION_DURATION_MS = 1000;
const SCROLL_OFFSET_PX = 600;
const CARD_ANIMATION_CLASS = "animate-missing-data";

export type CropCardRefMap = Map<string, HTMLDivElement | null>;

export const useMissingDataNavigation = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const cropCardRefMap = useRef<CropCardRefMap>();

  // This pattern follows the react docs: https://react.dev/learn/manipulating-the-dom-with-refs#how-to-manage-a-list-of-refs-using-a-ref-callback
  const getCropCardRefMap = useCallback((): CropCardRefMap => {
    if (!cropCardRefMap.current) {
      cropCardRefMap.current = new Map();
    }
    return cropCardRefMap.current;
  }, []);

  const scrollToItem = useCallback((node: HTMLDivElement) => {
    if (!scrollRef.current) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const elementPosition = rect.left - SCROLL_OFFSET_PX;

    scrollRef.current?.scrollBy({ left: elementPosition });

    const cardElement = node.getElementsByClassName("ant-card")[0];
    if (!cardElement) {
      return;
    }

    cardElement.classList.add(CARD_ANIMATION_CLASS);

    setTimeout(() => {
      cardElement.classList.remove(CARD_ANIMATION_CLASS);
    }, CARD_ANIMATION_DURATION_MS);
  }, []);

  const handleShowMissingDataYear = useCallback(
    (groupOrFieldId: string | number, itemId: string) => {
      const map = getCropCardRefMap();
      const key = typeof groupOrFieldId === "number" ? JSON.stringify({ group: groupOrFieldId, itemId }) : JSON.stringify({ fieldId: groupOrFieldId, itemId });
      const node = map.get(key);

      if (node) {
        scrollToItem(node);
      }
    },
    [getCropCardRefMap, scrollToItem]
  );

  const cropCardRefCallback = useCallback(
    (getCropCardRefMap: () => CropCardRefMap) => (node: HTMLDivElement | null, groupOrFieldId: string | number, itemId: string) => {
      if (!node) {
        return;
      }
      const map = getCropCardRefMap();
      const key = typeof groupOrFieldId === "number" ? JSON.stringify({ group: groupOrFieldId, itemId }) : JSON.stringify({ fieldId: groupOrFieldId, itemId });
      if (map.has(key)) {
        map.delete(key);
      }
      map.set(key, node);
    },
    []
  );

  const getEarliestItemIdWithMissingDataSingleView = useCallback(getEarliestItemIdWithMissingData, []);
  const getItemsWithMissingDataByFieldIdMultiView = useCallback(getItemsWithMissingDataByFieldId, []);

  return {
    scrollRef,
    getCropCardRefMap,
    handleShowMissingDataYear,
    getEarliestItemIdWithMissingDataSingleView,
    getItemsWithMissingDataByFieldIdMultiView,
    cropCardRefCallback,
  };
};
